import React, { useContext, useEffect, useState } from 'react';
import { get, isEqual } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Base } from 'src/components';
import { Table } from 'src/componentsTailwind';
import { TYPES } from 'src/componentsTailwind/Table/Table';
import { buildRows, handleLookup } from 'src/utils/goalListUtils';
import { useAccount } from 'src/queries/account';
import { useGoals, useGoalsLookup } from 'src/queries/goal';
import { useTree } from 'src/queries/tree';
import { GoalsContext } from 'src/containers/Goals/context/GoalsProvider';
import STYLE from 'src/constants/style';

export const dateKeys = {
  createdDate: {
    id: 'createdDate',
    label: 'Created Date'
  },
  lastUpdated: {
    id: 'lastUpdated',
    label: 'Last Updated'
  },
  deadline: {
    id: 'deadline',
    label: 'Due Date'
  }
};

const GoalsList = () => {
  const navigate = useNavigate();
  const { context, updateContext } = useContext(GoalsContext);
  const { dateKey, sort } = context;
  const [expandedGoals, setExpandedGoals] = useState(new Map());
  const [formattedGoals, setFormattedGoals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lookupData, setLookupData] = useState(new Map());
  const [subgoalFilter, setSubgoalFilter] = useState({
    baseId: null,
    treeId: null
  });

  const {
    data: goals,
    meta: {
      page: { totalPages }
    },
    isFetching: isFetchingGoals,
    isError: isErrorGoals
  } = useGoals(
    {
      isTopLevel: true,
      ...context
    },
    {
      page: {
        number: currentPage,
        size: 10
      },
      sort: {
        field: sort.field,
        order: sort.order
      }
    }
  );

  const {
    data: { goalTree },
    isFetching: isFetchingGoalsLookup,
    refetch: refetchGoalsLookup
  } = useGoalsLookup(
    {
      baseId: subgoalFilter.baseId,
      treeId: subgoalFilter.treeId
    },
    {
      lookup: {
        direction: 'down'
      }
    },
    { enabled: false }
  );

  const {
    data: viewerAccount,
    isFetching: isFetchingViewerAccount,
    isError: isErrorViewerAccount
  } = useAccount('me');

  useEffect(() => {
    if (goals) {
      const shouldUpdateFormattedGoals = !isEqual(formattedGoals, goals);
      const newGoals = goals.map((goal) => ({
        ...goal,
        children: []
      }));
      if (shouldUpdateFormattedGoals) {
        setExpandedGoals(new Map());
        setFormattedGoals(newGoals);
      }
    }
  }, [goals]);

  useEffect(() => {
    handleLookup({
      goalTree,
      isFetchingGoalsLookup,
      setLookupData,
      subgoalFilter,
      lookupData
    });
  }, [goalTree, isFetchingGoalsLookup]);

  const {
    data: { tree },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const isFetching = isFetchingGoals || isFetchingTree || isFetchingViewerAccount;
  const isError = isErrorGoals || isErrorTree || isErrorViewerAccount;
  const isReady = get(tree, 'id') && goals && !isFetching && !isError;

  const updateSort = (newSort) => {
    updateContext({ sort: { field: dateKey, order: newSort } });
    setCurrentPage(1);
    setExpandedGoals(new Map());
  };

  const updatePage = (newPage) => {
    setCurrentPage(newPage);
    setExpandedGoals(new Map());
  };

  const columns = [
    {
      label: 'Expand Row',
      hidden: true,
      span: 0.2,
      tooltip: true
    },
    {
      label: 'Title',
      tooltip: false
    },
    {
      label: 'Assignee',
      tooltip: true,
      span: 0.7
    },
    {
      label: dateKeys[dateKey].label,
      span: 0.7,
      tooltip: false,
      type: TYPES.SORTABLE,
      sort: { ...sort, isSelected: true },
      setSort: updateSort
    },
    {
      label: 'Progress',
      span: 0.5,
      tooltip: true
    },
    {
      label: 'Status',
      span: 0.5,
      tooltip: false
    },
    {
      type: TYPES.ACTION,
      label: 'Actions',
      span: 0.4,
      hidden: true,
      tooltip: false
    }
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [context]);

  const rows = buildRows({
    viewerAccount,
    goalList: formattedGoals,
    expandedGoals,
    lookupData,
    tree,
    refetchGoalsLookup,
    setSubgoalFilter,
    setExpandedGoals,
    navigate,
    dateKey
  });

  if (!isReady) {
    return null;
  }
  const pagination = {
    currentPage,
    setCurrentPage: updatePage,
    totalPages
  };

  return (
    <Base classes={STYLE.CONTAINER_WHITE}>
      <Table
        columns={columns}
        rows={rows}
        rowClasses='!px-0 [&>*:first-child]:!pl-4 [&>*:last-child]:!pr-0'
        headClasses='[&_*]:!text-sm [&_*]:!truncate [&>*:first-child]:!pl-0 [&>*:last-child]:!pr-0'
        pagination={pagination}
      />
    </Base>
  );
};

export default GoalsList;
